import React, { useEffect, useState } from 'react';
import { projects } from '../data/mockData';
import { COLORS, SPACING } from '../constants/designSystem';
import { Badge } from '../components/ui/badge';
import { ExternalLink } from 'lucide-react';

const ProjectsPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const categories = [
    { id: 'all', label: 'All Projects' },

    // Core Engineering Areas
    { id: 'fintech', label: 'Fintech' },
    { id: 'enterprise', label: 'Enterprise Systems' },
    { id: 'logistics', label: 'Logistics & SaaS' },
    { id: 'education', label: 'Education / Cybersecurity' },

    // Creative & Technical Fields
    { id: 'gaming', label: 'Gaming' },
    { id: 'cinematography', label: 'Cinematography' },
    { id: 'music', label: 'Music' },
    { id: 'iot', label: 'IoT' },
    { id: 'os', label: 'Operating Systems' },
  ];

  const allProjects = Object.entries(projects).flatMap(([category, items]) => items);
  const filteredProjects =
    selectedCategory === 'all'
      ? allProjects
      : projects[selectedCategory] || [];

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh', paddingTop: '80px' }}>
      {/* Hero */}
      <section
        style={{
          padding: `${SPACING.xxxl} ${SPACING.lg}`,
          textAlign: 'center',
          background: `radial-gradient(circle at 50% 50%, ${COLORS.primary}08 0%, transparent 60%)`,
        }}
      >
        <div
          style={{
            maxWidth: '800px',
            margin: '0 auto',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <div
            style={{
              display: 'inline-block',
              padding: `${SPACING.xs} ${SPACING.md}`,
              backgroundColor: `${COLORS.primary}15`,
              border: `1px solid ${COLORS.primary}40`,
              borderRadius: '50px',
              marginBottom: SPACING.md,
            }}
          >
            <span style={{ fontSize: '14px', color: COLORS.primary, fontWeight: '500' }}>My Work</span>
          </div>
          <h1
            style={{
              fontSize: '56px',
              fontWeight: '700',
              color: COLORS.text.primary,
              marginBottom: SPACING.md,
            }}
          >
            Project Portfolio
          </h1>
          <p
            style={{
              fontSize: '18px',
              lineHeight: '1.6',
              color: COLORS.text.secondary,
            }}
          >
            From fintech platforms to creative experiments—a collection of work spanning development, design, and innovation
          </p>
        </div>
      </section>

      {/* Filter */}
      <section style={{ padding: `0 ${SPACING.lg} ${SPACING.xl}` }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              gap: SPACING.sm,
              flexWrap: 'wrap',
              justifyContent: 'center',
              padding: SPACING.lg,
              backgroundColor: COLORS.surface,
              borderRadius: '16px',
              border: `1px solid ${COLORS.border}`,
            }}
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                style={{
                  padding: `${SPACING.sm} ${SPACING.lg}`,
                  backgroundColor:
                    selectedCategory === cat.id ? COLORS.primary : 'transparent',
                  color:
                    selectedCategory === cat.id
                      ? COLORS.background
                      : COLORS.text.secondary,
                  border: `1px solid ${selectedCategory === cat.id ? COLORS.primary : COLORS.border}`,
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 300ms',
                }}
                onMouseEnter={(e) => {
                  if (selectedCategory !== cat.id) {
                    e.currentTarget.style.borderColor = COLORS.primary;
                    e.currentTarget.style.color = COLORS.primary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCategory !== cat.id) {
                    e.currentTarget.style.borderColor = COLORS.border;
                    e.currentTarget.style.color = COLORS.text.secondary;
                  }
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section style={{ padding: `${SPACING.xl} ${SPACING.lg} ${SPACING.xxxl}` }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
              gap: SPACING.lg,
            }}
            className="projects-grid"
          >
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                style={{
                  backgroundColor: COLORS.cardSurface,
                  borderRadius: '20px',
                  border: `1px solid ${COLORS.border}`,
                  overflow: 'hidden',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 500ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 50}ms`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.borderColor = COLORS.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = COLORS.border;
                }}
              >
                <div
                  style={{
                    height: '260px',
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: COLORS.surface,
                  }}
                >
                  <img
                    src={project.image}
                    alt={project.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: `linear-gradient(180deg, transparent 0%, ${COLORS.cardSurface}90 100%)`,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: SPACING.md,
                      right: SPACING.md,
                    }}
                  >
                    <Badge
                      style={{
                        backgroundColor: `${COLORS.primary}90`,
                        color: COLORS.background,
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '6px 12px',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      {project.category}
                    </Badge>
                  </div>
                  {project.year && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: SPACING.md,
                        left: SPACING.md,
                        padding: '6px 12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        backdropFilter: 'blur(10px)',
                        color: COLORS.text.primary,
                        fontSize: '12px',
                        fontWeight: '600',
                        borderRadius: '6px',
                      }}
                    >
                      {project.year}
                    </div>
                  )}
                </div>

                <div style={{ padding: SPACING.lg }}>
                  <h3
                    style={{
                      fontSize: '22px',
                      lineHeight: '1.3',
                      fontWeight: '600',
                      color: COLORS.text.primary,
                      marginBottom: SPACING.sm,
                    }}
                  >
                    {project.title}
                  </h3>

                  <p
                    style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: COLORS.text.secondary,
                      marginBottom: SPACING.md,
                    }}
                  >
                    {project.description}
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: SPACING.xs,
                      marginBottom: SPACING.md,
                    }}
                  >
                    {project.techStack.slice(0, 4).map((tech, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: '12px',
                          color: COLORS.text.secondary,
                          backgroundColor: COLORS.surface,
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: `1px solid ${COLORS.border}`,
                        }}
                      >
                        {tech}
                      </span>
                    ))}
                    {project.techStack.length > 4 && (
                      <span
                        style={{
                          fontSize: '12px',
                          color: COLORS.primary,
                          backgroundColor: `${COLORS.primary}15`,
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: `1px solid ${COLORS.primary}40`,
                        }}
                      >
                        +{project.techStack.length - 4}
                      </span>
                    )}
                  </div>

                  <button
                    style={{
                      color: COLORS.primary,
                      fontSize: '14px',
                      fontWeight: '600',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: 0,
                      transition: 'gap 300ms',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.gap = '10px';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.gap = '6px';
                    }}
                    onClick={() => {if (project.link) { window.open(project.link, '_blank'); }}}
                  >
                    View Details <ExternalLink size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .projects-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProjectsPage;
